import { PERMISSIONS } from "consts";
import { ButtonProps } from "fe-shared/src/components";
import { CheckIcon, CloseIcon } from "fe-shared/src/components/Icons";
import {
  hasAnyPermission,
  hasPermission,
  hasAllPermissions,
  getFullPermissionCode
} from "fe-shared/src/helpers/permissions";

import {
  PermissionLevel,
  UserPermissionsDictionary,
} from "fe-shared/src/types/domain";
import { InvoiceApprovalThreshold } from "fe-shared/src/types/common/invoice";
import { Invoice, InvoiceStatusCode } from "fe-shared-transactions/src/types";
import { isInRange } from "helpers/invoiceApprovalThresholdsChecker";

import styles from "../InvoicesDetailsView.module.scss";

export enum InvoiceAction {
  FirstApprove = "FirstApprove",
  SecondApprove = "SecondApprove",
  PostToGP = "PostToGP",
  Cancel = "Cancel",
  Adjust = "Adjust",
};

const approvingPermissions : string[] = [
  PERMISSIONS.invoiceApproveClerkLevel,
  PERMISSIONS.invoiceApproveManagerLevel,
  PERMISSIONS.invoiceApproveDirectorLevel,
  PERMISSIONS.invoiceApproveExecutiveLevel,
  PERMISSIONS.invoiceApproveCEOLevel,
];

const GPPostingPermissions : string[] = [
  PERMISSIONS.invoicePostToGP,
];

export interface ActionConfig {
  name: InvoiceAction;
  isVisible: (
    invoice: Invoice,
    invoiceApprovalThresholds: InvoiceApprovalThreshold[],
    permissions?: UserPermissionsDictionary,
    isGPEnabled?: boolean
  ) => boolean;
  hasConfirmationDialog: boolean;
  buttonProps: Partial<ButtonProps>;
  dialogButtonProps?: Partial<ButtonProps>;
}

export const actions: ActionConfig[] = [
  {
    name: InvoiceAction.FirstApprove,
    isVisible: (invoice, invoiceApprovalThresholds, permissions, isGPEnabled) => {

      const validPermissions = invoiceApprovalThresholds
        .filter(threshold => threshold.approvalTypeCode === InvoiceStatusCode.InApproval)
        .filter(threshold => 
          isInRange(
            invoice.total,
            threshold.from,
            threshold.to
          )).map(threshold => 
                  getFullPermissionCode(approvingPermissions, threshold.roleCode));
      
      return (
        invoice.statusCode === InvoiceStatusCode.InReview &&
        hasAllPermissions(
          permissions,
          validPermissions.map((name) => ({
            level: PermissionLevel.Full,
            name,
          }))
        )
      );
    },
    hasConfirmationDialog: true,
    buttonProps: {
      variant: "filled",
      iconComponent: CheckIcon,
    },
    dialogButtonProps: {
      variant: "filled",
      iconComponent: CheckIcon,
    },
  },
  {
    name: InvoiceAction.SecondApprove,
    isVisible: (invoice, invoiceApprovalThresholds, permissions, isGPEnabled) => {
      const validPermissions = invoiceApprovalThresholds
        .filter(threshold => threshold.approvalTypeCode === InvoiceStatusCode.Approved)
        .filter(threshold => 
          isInRange(
            invoice.total,
            threshold.from,
            threshold.to
        )).map(threshold => 
                getFullPermissionCode(approvingPermissions, threshold.roleCode));

      return (
        invoice.statusCode === InvoiceStatusCode.InApproval &&
        hasAllPermissions(
          permissions,
          validPermissions.map((name) => ({
            level: PermissionLevel.Full,
            name,
          }))
        )
      );
    },
    hasConfirmationDialog: true,
    buttonProps: {
      variant: "filled",
      iconComponent: CheckIcon,
    },
    dialogButtonProps: {
      variant: "filled",
      iconComponent: CheckIcon,
    },
  },
  {
    name: InvoiceAction.PostToGP,
    isVisible: (invoice, invoiceApprovalThresholds, permissions, isGPEnabled) => {

      if (!isGPEnabled) {
        return false;
      }
      const validPermissions = GPPostingPermissions;

      const isPostedToGP : boolean = !!invoice.isPostedToGP;

      return (
        invoice.statusCode === InvoiceStatusCode.Approved &&
        !isPostedToGP &&
        hasAnyPermission(
          permissions,
          validPermissions.map((name) => ({
            level: PermissionLevel.Full,
            name,
          }))
        )
      );
    },
    hasConfirmationDialog: true,
    buttonProps: {
      variant: "filled",
      iconComponent: CheckIcon,
      className: styles.approve,
    },
    dialogButtonProps: {
      variant: "filled",
      iconComponent: CheckIcon,
    },
  },
  {
    name: InvoiceAction.Cancel,
    isVisible: (invoice, invoiceApprovalThresholds, permissions, isGPEnabled) => {
      const validPermissions = approvingPermissions;

      return (
        invoice.statusCode === InvoiceStatusCode.InApproval &&
        hasAnyPermission(
          permissions,
          validPermissions.map((name) => ({
            level: PermissionLevel.Full,
            name,
          }))
        )
      );
    },
    hasConfirmationDialog: true,
    buttonProps: {
      variant: "filled",
      iconComponent: CloseIcon,
      className: styles.cancel,
    },
    dialogButtonProps: {
      variant: "filled",
      iconComponent: CheckIcon,
    },
  },
  {
    name: InvoiceAction.Adjust,
    isVisible: (invoice, invoiceApprovalThresholds, permissions, isGPEnabled) =>
      invoice.statusCode === InvoiceStatusCode.InReview &&
      hasPermission(permissions, {
        level: PermissionLevel.Full,
        name: PERMISSIONS.invoiceAdjust,
      }),
    hasConfirmationDialog: true,
    buttonProps: {
      variant: "outlined",
    },
  },
];
